import { useMemo } from "react";

import clsx from "clsx";
import { mean, orderBy } from "lodash";

import { TitleSlide } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import {
  type MetricPoint,
  addToMetricPoint,
  newMetricPoint,
} from "@north-beam/nb-common/metrics";
import { MetricValue } from "@shared/metric-value";
import Spinner from "@shared/spinner";

import { AttributionOptionsSelect } from "../attribution-options-select";
import { useUniversalBenchmarksConfiguratorContext } from "../hooks";
import {
  type AdoptableMetrics,
  type AdoptedMetrics,
  type AdoptedMetricsData,
  type DaySelection,
} from "../types";

interface Props {
  adoptedMetric: AdoptedMetrics;
  adoptedMetricsData?: AdoptedMetricsData;
  daysSelection: DaySelection[];
  adoptableKpiMetrics: AdoptableMetrics;
}

export const KpiCard = ({
  adoptedMetric,
  adoptedMetricsData,
  adoptableKpiMetrics,
  daysSelection,
}: Props) => {
  const { designElementManager } = useDesignElementManager();

  const {
    kpiAttributionModel,
    kpiAttributionWindow,
    kpiAccountingMode,
    setKpiAttributionModel,
    setKpiAttributionWindow,
    setKpiAccountingMode,
  } = useUniversalBenchmarksConfiguratorContext();

  const metric = useMemo(() => {
    return adoptableKpiMetrics.metrics.find(
      (e) => e.id === adoptedMetric.metricId,
    );
  }, [adoptedMetric, adoptableKpiMetrics]);

  const spendMetric = useMemo(() => {
    return adoptableKpiMetrics.spend;
  }, [adoptedMetric, adoptableKpiMetrics]);

  const revMetric = useMemo(() => {
    return adoptableKpiMetrics.rev;
  }, [adoptedMetric, adoptableKpiMetrics]);

  const { metricValue, valuesByPlatform } = useMemo(() => {
    const rows = adoptedMetricsData?.data?.rows;

    if (!rows || !metric) {
      return { metricValue: 0, valuesByPlatform: [] } as const;
    }

    const excludedDays = daysSelection
      .filter((e) => e.isExcluded || e.willBeExcluded)
      .map((e) => e.day);

    const valuesByDay: Record<string, MetricPoint> = {};
    const valuesByPlatform = [];

    for (const row of rows) {
      const rowValues = [];
      let spend = 0;
      let rev = 0;

      for (const metricPoint of row.metrics) {
        if (excludedDays.includes(metricPoint.date)) {
          continue;
        }

        if (!valuesByDay[metricPoint.date]) {
          valuesByDay[metricPoint.date] = newMetricPoint();
        }

        addToMetricPoint(valuesByDay[metricPoint.date], metricPoint);

        const rowVal = metric.calculate(metricPoint);
        rowValues.push(isNaN(rowVal) || !isFinite(rowVal) ? 0 : rowVal);

        const spendVal = spendMetric?.calculate(metricPoint) ?? 0;
        spend += isNaN(spendVal) || !isFinite(spendVal) ? 0 : spendVal;

        const revVal = revMetric?.calculate(metricPoint) ?? 0;
        rev += isNaN(revVal) || !isFinite(revVal) ? 0 : revVal;
      }

      valuesByPlatform.push({
        platform: row.dimensions["Platform (Northbeam)"],
        value: mean(rowValues),
        spend,
        rev,
      });
    }

    const values = Object.values(valuesByDay).map((e) => metric.calculate(e));

    return {
      metricValue: mean(values),
      valuesByPlatform: orderBy(
        valuesByPlatform.filter(
          (e) => e.spend > 0 && e.rev > 0 && e.value > 0.005,
        ),
        [(row) => row.value],
        [metric.isPositiveChangeGood ? "desc" : "asc"],
      ),
    } as const;
  }, [adoptedMetricsData, metric, daysSelection]);

  if (!metric) {
    return (
      <div className="relative border border-v2-tertiary rounded-lg w-52 py-4 px-5 h-52">
        <TitleSlide className="text-xs text-center">
          Could not find this Traffic Source.
        </TitleSlide>
      </div>
    );
  }

  const isLoading =
    !adoptedMetricsData || adoptedMetricsData.status === "loading";

  const metricName =
    metric.metadata.overviewPageMetricName ||
    metric.metadata.name ||
    metric.name;

  return (
    <div className="relative border border-v2-tertiary rounded-lg p-4 flex flex-col max-h-full">
      <div className="font-bold mb-1">{metricName}</div>
      {isLoading ? (
        <div className="flex items-center justify-center h-24">
          <Spinner />
        </div>
      ) : (
        <div className="flex-1 flex flex-col gap-4 overflow-hidden">
          <div className="flex-1 shrink-0 flex items-center text-4xl text-v2-action font-bold">
            <GenericTooltip
              className="truncate"
              noInfoCircle
              mouseEnterDelay={1000}
              placement="top"
              content={
                <MetricValue
                  as="span"
                  format={metric.format}
                  value={metricValue}
                  precision="exact"
                />
              }
            >
              <MetricValue
                className="truncate text-v2-primary"
                as="span"
                format={metric.format}
                value={metricValue}
                precision="exact"
              />
            </GenericTooltip>
          </div>
          <div>
            <AttributionOptionsSelect
              attributionModel={kpiAttributionModel}
              attributionWindow={kpiAttributionWindow}
              accountingMode={kpiAccountingMode}
              onAttributionModelChange={setKpiAttributionModel}
              onAttributionWindowChange={setKpiAttributionWindow}
              onAccountingModeChange={setKpiAccountingMode}
            />
          </div>
          <div className="flex-1 overflow-hidden flex flex-col">
            <div
              className={clsx(
                "flex items-center justify-between text-xxs border-b border-v2-tertiary",
                "py-2.5",
              )}
            >
              <div className="font-bold">CHANNEL BREAKDOWN</div>
              <div>{metricName}</div>
            </div>
            <div className="flex-1 text-xxs overflow-y-auto">
              {valuesByPlatform.map((row, index) => {
                return (
                  <div
                    key={row.platform}
                    className={clsx(
                      "flex items-center justify-between gap-4",
                      "pt-2",
                      index !== valuesByPlatform.length - 1 &&
                        "border-b border-v2-tertiary pb-2",
                    )}
                  >
                    <div className="flex items-center gap-2 truncate">
                      {designElementManager
                        .getDesignElement(row.platform)
                        .getComponent({ size: "20px" })}
                      <div className="truncate">{row.platform}</div>
                    </div>
                    <div className="shrink-0">
                      <MetricValue
                        as="span"
                        format={metric.format}
                        value={row.value}
                        precision="exact"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
